import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ChevronDown,
  ExternalLink,
} from 'lucide-react'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { useAppStore } from '@/stores'
import { usePermissions } from '@/hooks/use-permissions'
import { useMyVendorIds } from '@/hooks/use-vendor-portal'
import { useArchiveBoards } from '@/hooks'
import { cn } from '@/lib/utils'
import { OpsyriLogo } from '@/components/ui/opsyri-logo'
import {
  mainNavItems, cmmsNavItems, archivesCmmsItem, planningNavItems,
  managementNavItems, salesNavItems, toolsNavItems, settingsNavItems,
  vendorPortalNavItems, clientPortalNavItems, type NavItem,
} from './nav-items'

// Shared with the desktop Sidebar — see ./nav-items.
export function MobileSidebar() {
  const location = useLocation()
  const { mobileMenuOpen, closeMobileMenu } = useAppStore()
  const { isVendor, isClient, canAccess, canAccessInspections, isCornerstoneOrg } = usePermissions()
  const { data: myVendors } = useMyVendorIds()
  const { data: archiveBoards } = useArchiveBoards()
  const hasArchives = (archiveBoards?.length ?? 0) > 0
  const hasVendorLink = (myVendors?.length ?? 0) > 0

  // Section collapse, matching the desktop sidebar. An admin sees 41 nav items
  // here; without this the drawer is one long scroll with no way to shorten it.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toggleSection = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))

  const filterByPermission = (items: NavItem[]) =>
    items.filter(item => !item.page || canAccess(item.page))

  const NavLink = ({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')
    return (
      <Link
        to={item.href}
        onClick={closeMobileMenu}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          'hover:bg-accent hover:text-accent-foreground active:bg-accent/80',
          isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'
        )}
      >
        <item.icon className="h-5 w-5 shrink-0" />
        <span>{item.title}</span>
      </Link>
    )
  }

  const NavSection = ({ label, sectionKey, items }: { label: string; sectionKey: string; items: NavItem[] }) => {
    const filtered = filterByPermission(items)
    if (filtered.length === 0) return null
    const isCollapsed = collapsed[sectionKey] ?? false

    return (
      <>
        <button
          onClick={() => toggleSection(sectionKey)}
          className="mt-5 mb-1.5 px-3 w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 hover:text-foreground transition-colors"
        >
          <span>{label}</span>
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform duration-200', isCollapsed && '-rotate-90')}
          />
        </button>
        {!isCollapsed && (
          <nav className="space-y-0.5">
            {filtered.map((item) => <NavLink key={item.href} item={item} />)}
          </nav>
        )}
      </>
    )
  }

  return (
    <Sheet open={mobileMenuOpen} onOpenChange={(open) => !open && closeMobileMenu()}>
      <SheetContent side="left" className="flex w-72 flex-col p-0">
        <SheetTitle className="sr-only">Navigation</SheetTitle>

        {/* Logo header */}
        <div className="flex h-16 items-center gap-2 border-b px-4">
          <OpsyriLogo className="h-7" />
        </div>

        {/* SheetContent is now flex-col, so this takes the leftover height
            instead of a hardcoded 100vh-8rem. That guess assumed a 4rem footer;
            the footer is 105px, which pushed 29px of the Settings row below the
            fold. min-h-0 because a flex child defaults to min-height:auto and
            would otherwise refuse to shrink under its content. */}
        <ScrollArea className="min-h-0 flex-1">
          <div className="px-3 py-3">
            {isVendor ? (
              <nav className="space-y-0.5">
                {vendorPortalNavItems.map(item => <NavLink key={item.href} item={item} />)}
              </nav>
            ) : isClient ? (
              <nav className="space-y-0.5">
                {clientPortalNavItems
                  .filter(item =>
                    item.href === '/portal/client/inspections' ? canAccessInspections
                    : item.href === '/portal/client/tours' ? isCornerstoneOrg
                    : true,
                  )
                  .map(item => <NavLink key={item.href} item={item} />)}
              </nav>
            ) : (
              <>
                <nav className="space-y-0.5">
                  {filterByPermission(mainNavItems).map(item => <NavLink key={item.href} item={item} />)}
                </nav>

                <NavSection
                  label="CMMS"
                  sectionKey="cmms"
                  items={
                    hasArchives
                      ? [cmmsNavItems[0], archivesCmmsItem, ...cmmsNavItems.slice(1)]
                      : cmmsNavItems
                  }
                />
                <NavSection label="Planning" sectionKey="planning" items={planningNavItems} />
                <NavSection label="Management" sectionKey="management" items={managementNavItems} />
                <NavSection label="Sales" sectionKey="sales" items={salesNavItems} />
                <NavSection label="Tools" sectionKey="tools" items={toolsNavItems} />

                {/* Team / Roles / Workflow / Settings / Billing live here rather
                    than in a pinned footer. Pinning them would re-create the bug
                    this fixes: the footer competing with the nav for a fixed
                    slice of a short screen. Inside the scroll area nothing can
                    be clipped, however many entries an admin has. */}
                <NavSection label="Admin & Settings" sectionKey="settings" items={settingsNavItems} />

                {hasVendorLink && (
                  <>
                    <Separator className="my-3" />
                    <Link
                      to="/portal/vendor"
                      onClick={closeMobileMenu}
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                        'border border-primary/20 bg-primary/10 text-primary hover:bg-primary/15 active:bg-primary/15',
                        location.pathname.startsWith('/portal/vendor') && 'ring-1 ring-primary/40',
                      )}
                    >
                      <ExternalLink className="h-5 w-5 shrink-0" />
                      <span>Vendor Portal</span>
                    </Link>
                  </>
                )}
              </>
            )}
          </div>
        </ScrollArea>

      </SheetContent>
    </Sheet>
  )
}
